import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import axios from 'axios';
import { io, Socket } from 'socket.io-client';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5000');

// ─── Interfaces ──────────────────────────────────────────────────────────────

interface S3RecordingDetail {
  applicationId: string;
  userEmail: string;
  status: string;
  originalSizeBytes: number;
  s3SizeBytes: number;
  s3Key: string;
  totalEvents: number;
  createdAt: string;
  migratedAt: string;
}

interface MongoRecordingDetail {
  _id: string;
  applicationId: string;
  userEmail: string;
  status: string;
  totalSizeBytes: number;
  totalChunks: number;
  totalEvents: number;
  storageType: string;
  createdAt: string;
}

interface S3StatusData {
  s3: {
    recordings: number;
    compressedSizeMB: string;
    originalSizeMB: string;
    compressionRatio: string;
    recentMigrations: S3RecordingDetail[];
  };
  mongodb: {
    recordings: number;
    mongoOnly: number;
    sizeMB: string;
    remainingChunks: number;
    details: MongoRecordingDetail[];
  };
  overview: {
    totalRecordings: number;
    emptyRecordings: number;
    activelyRecording: number;
    migratableCount: number;
    migratableSizeMB: string;
  };
}

interface MigrationQueueItem {
  applicationId: string;
  userEmail: string;
  totalSizeBytes: number;
  totalChunks: number;
  status: 'queued' | 'migrating' | 'done' | 'cancelled';
}

interface MigrationProgress {
  active: boolean;
  cancelled: boolean;
  total: number;
  currentIndex: number;
  currentRecording: { applicationId: string; userEmail: string; totalSizeBytes: number; totalChunks: number } | null;
  currentStep: string;
  chunkProgress: { current: number; total: number; eventsSoFar: number; bytesSoFar: number };
  stepProgress: { percent: number; detail: string };
  completedCount: number;
  failedCount: number;
  completed: { applicationId: string; s3SizeBytes: number; originalSizeBytes: number; duration: number }[];
  failed: { applicationId: string; error: string }[];
  totalBytes: number;
  freedBytes: number;
  startedAt: number | null;
  queue: MigrationQueueItem[];
}

// Uploads Backup interfaces
interface BackupFolderInfo {
  name: string;
  files: number;
  size: number;
  sizeFormatted: string;
}

interface BackupStatusData {
  local: { totalFiles: number; totalSize: number; totalSizeFormatted: string };
  s3: { totalFiles: number; totalSize: number; totalSizeFormatted: string };
  inSync: boolean;
  folders: BackupFolderInfo[];
  lastSyncLog: string;
  lastSyncAt: string | null;
}

const formatBackupBytes = (bytes: number): string => {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return (bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0) + ' ' + units[i];
};

/** Convert any date to IST Indian format — "18th Mar'26, 2:30 AM" */
const formatToIST = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  // IST = UTC + 5:30
  const ist = new Date(d.getTime() + (5.5 * 60 * 60 * 1000));
  const day = ist.getUTCDate();
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const month = months[ist.getUTCMonth()];
  const year = String(ist.getUTCFullYear()).slice(2);
  const suffix = (day === 1 || day === 21 || day === 31) ? 'st' : (day === 2 || day === 22) ? 'nd' : (day === 3 || day === 23) ? 'rd' : 'th';
  let hrs = ist.getUTCHours();
  const mins = String(ist.getUTCMinutes()).padStart(2, '0');
  const ampm = hrs >= 12 ? 'PM' : 'AM';
  hrs = hrs % 12 || 12;
  return `${day}${suffix} ${month}'${year}, ${hrs}:${mins} ${ampm}`;
};

// Database Backup interfaces
interface DbBackupEntry {
  filename: string;
  date: string;
  time: string;
  dateISO: string;
  size: number;
  sizeFormatted: string;
  location: 's3' | 'local';
}

interface DbBackupData {
  s3Backups: DbBackupEntry[];
  localBackups: DbBackupEntry[];
  collectionsSummary: Record<string, number> | null;
  lastSyncLog: string;
  lastBackupAt: string | null;
  retentionDays: { s3: number; local: number };
}


/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   Keyframes
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

const pulse = keyframes`0%,100%{opacity:1}50%{opacity:0.6}`;
const s3Glow = keyframes`0%,100%{box-shadow:0 0 0 0 rgba(99,102,241,0)}50%{box-shadow:0 0 12px 2px rgba(99,102,241,0.15)}`;
const shimmer = keyframes`0%{background-position:-200% 0}100%{background-position:200% 0}`;
const fadeSlideIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;
const migrationPulse = keyframes`0%,100%{opacity:0.6}50%{opacity:1}`;
const progressStripe = keyframes`0%{background-position:0 0}100%{background-position:40px 0}`;
const countdownPulse = keyframes`0%,100%{transform:scale(1)}50%{transform:scale(1.05)}`;
const progressFill = keyframes`0%{width:0%}100%{width:100%}`;
const shimmerGlow = keyframes`0%,100%{box-shadow:0 0 20px rgba(99,102,241,0.2),inset 0 0 10px rgba(99,102,241,0.1)}50%{box-shadow:0 0 40px rgba(99,102,241,0.4),inset 0 0 15px rgba(99,102,241,0.2)}`;
/* slideInRight removed — was for NotificationToast, now handled globally */

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   Page-level Styled Components
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

const PageContainer = styled.div`
  width: 100%;
  padding: 0;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
  svg { width: 24px; height: 24px; color: #6366f1; }
`;

const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const TopTabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 18px;
`;

const TopTab = styled.button<{ $active: boolean }>`
  padding: 10px 22px;
  border: none;
  background: none;
  font-size: 13px;
  font-weight: ${p => p.$active ? 600 : 500};
  color: ${p => p.$active ? '#6366f1' : '#94a3b8'};
  border-bottom: 2px solid ${p => p.$active ? '#6366f1' : 'transparent'};
  margin-bottom: -2px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { color: #6366f1; }
`;

const LiveDot = styled.span`display:inline-block;width:6px;height:6px;border-radius:50%;background:#22c55e;animation:${pulse} 2s ease-in-out infinite;`;

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   S3 Storage Management — Styled Components (copied from AdminStorageStats)
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

const S3Section = styled.div`margin-bottom: 14px;`;

const S3Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  padding: 16px 18px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const S3StatCard = styled.div<{ $accent?: string; $glow?: boolean }>`
  background: ${p => p.$accent ? `${p.$accent}08` : '#f8fafc'};
  border: 1px solid ${p => p.$accent ? `${p.$accent}25` : '#e2e8f0'};
  border-radius: 12px;
  padding: 14px 16px;
  position: relative;
  overflow: hidden;
  animation: ${fadeSlideIn} 0.3s ease;
  ${p => p.$glow && css`animation: ${s3Glow} 3s ease-in-out infinite;`}
`;

const S3StatLabel = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
`;

const S3StatNumber = styled.div<{ $color?: string; $size?: string }>`
  font-size: ${p => p.$size || '22px'};
  font-weight: 700;
  color: ${p => p.$color || '#1e293b'};
  line-height: 1.2;
`;

const S3StatSub = styled.div`
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
`;

const S3DonutWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 18px 16px;
`;

const S3DonutLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const S3LegendItem = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #475569;
  &::before {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 3px;
    background: ${p => p.$color};
    flex-shrink: 0;
  }
`;

const S3BarTrack = styled.div`
  width: 100%;
  height: 8px;
  background: #f1f5f9;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
`;

const S3BarSegment = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  width: ${p => p.$pct}%;
  background: ${p => p.$color};
  transition: width 0.6s ease;
`;

const S3TableWrap = styled.div`
  padding: 0 18px 16px;
  overflow-x: auto;
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 2px; }
`;

const S3Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  th {
    text-align: left;
    padding: 6px 8px;
    font-weight: 600;
    color: #94a3b8;
    font-size: 10px;
    border-bottom: 1px solid #e2e8f0;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    white-space: nowrap;
  }
  td {
    padding: 7px 8px;
    border-bottom: 1px solid #f8fafc;
    color: #475569;
    white-space: nowrap;
  }
  tr:hover td { background: #f8fafc; }
`;

const S3StatusBadge = styled.span<{ $variant: 's3' | 'mongodb' | 'recording' | 'empty' | 'migratable' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
  ${p => {
    switch (p.$variant) {
      case 's3': return css`background: #dcfce7; color: #166534;`;
      case 'mongodb': return css`background: #fef3c7; color: #92400e;`;
      case 'recording': return css`background: #dbeafe; color: #1e40af;`;
      case 'empty': return css`background: #f1f5f9; color: #94a3b8;`;
      case 'migratable': return css`background: #fae8ff; color: #86198f;`;
    }
  }}
`;

const S3MigrateArea = styled.div`
  padding: 14px 18px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const S3MigrateBtn = styled.button<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: ${p => p.$disabled ? 'not-allowed' : 'pointer'};
  background: ${p => p.$disabled
    ? '#e2e8f0'
    : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'};
  color: ${p => p.$disabled ? '#94a3b8' : '#fff'};
  box-shadow: ${p => p.$disabled ? 'none' : '0 2px 8px rgba(99,102,241,0.3)'};
  transition: all 0.2s;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(99,102,241,0.4);
  }
`;

const S3ConfirmInput = styled.input`
  padding: 6px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 6px;
  font-size: 12px;
  font-family: monospace;
  width: 120px;
  outline: none;
  &:focus { border-color: #6366f1; }
`;

const S3InfoBanner = styled.div<{ $type?: 'info' | 'success' | 'warn' }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 11.5px;
  line-height: 1.5;
  color: ${p => p.$type === 'success' ? '#166534' : p.$type === 'warn' ? '#92400e' : '#475569'};
  background: ${p => p.$type === 'success' ? '#dcfce7' : p.$type === 'warn' ? '#fef3c7' : '#f0f4ff'};
  border: 1px solid ${p => p.$type === 'success' ? '#bbf7d0' : p.$type === 'warn' ? '#fde68a' : '#dbeafe'};
  svg { width: 14px; height: 14px; flex-shrink: 0; margin-top: 1px; }
`;

const S3TabBar = styled.div`
  display: flex;
  gap: 0;
  padding: 0 18px;
  border-bottom: 1px solid #e2e8f0;
`;

const S3Tab = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  border: none;
  background: none;
  font-size: 12px;
  font-weight: ${p => p.$active ? 600 : 500};
  color: ${p => p.$active ? '#6366f1' : '#94a3b8'};
  border-bottom: 2px solid ${p => p.$active ? '#6366f1' : 'transparent'};
  cursor: pointer;
  transition: all 0.15s;
  &:hover { color: #6366f1; }
`;

const S3RefreshBtn = styled.button`
  padding: 4px 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 6px;
  font-size: 11px;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.15s;
  &:hover { background: #f8fafc; border-color: #cbd5e1; }
  svg { width: 12px; height: 12px; }
`;

const S3ShimmerRow = styled.div`
  height: 24px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 4px;
  margin: 4px 18px;
`;

const MigrationPanel = styled.div`
  margin: 0 18px 16px;
  border: 1.5px solid #e9d5ff;
  border-radius: 12px;
  background: linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%);
  overflow: hidden;
  animation: ${fadeSlideIn} 0.3s ease;
`;

const MigrationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
`;

const MigrationProgressBar = styled.div`
  position: relative;
  height: 10px;
  background: #e9d5ff;
  border-radius: 5px;
  margin: 0 16px 12px;
  overflow: hidden;
`;

const MigrationProgressFill = styled.div<{ $pct: number; $active?: boolean }>`
  position: absolute;
  top: 0; left: 0; bottom: 0;
  width: ${p => p.$pct}%;
  background: ${p => p.$active
    ? 'linear-gradient(90deg, #8b5cf6 0%, #6366f1 50%, #8b5cf6 100%)'
    : '#22c55e'};
  background-size: 40px 40px;
  ${p => p.$active && css`animation: ${progressStripe} 1s linear infinite;`}
  border-radius: 5px;
  transition: width 0.6s ease;
`;

const MigrationStepBadge = styled.span<{ $step: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  ${p => {
    switch (p.$step) {
      case 'decompressing': return css`background: #fef3c7; color: #92400e;`;
      case 'compressing': return css`background: #dbeafe; color: #1e40af;`;
      case 'uploading': return css`background: #fae8ff; color: #86198f;`;
      case 'cleanup': return css`background: #fecaca; color: #991b1b;`;
      case 'done': return css`background: #dcfce7; color: #166534;`;
      case 'complete': return css`background: #dcfce7; color: #166534;`;
      case 'cancelled': return css`background: #fef3c7; color: #92400e;`;
      default: return css`background: #f1f5f9; color: #64748b;`;
    }
  }}
`;

const MigrationQueueList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  padding: 0 16px 12px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 2px; }
`;

const MigrationQueueItemStyled = styled.div<{ $status: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 11px;
  margin-bottom: 3px;
  transition: all 0.3s ease;
  background: ${p => p.$status === 'migrating' ? '#ede9fe' : p.$status === 'done' ? '#f0fdf4' : p.$status === 'cancelled' ? '#fef3c7' : '#fff'};
  border: 1px solid ${p => p.$status === 'migrating' ? '#c4b5fd' : p.$status === 'done' ? '#bbf7d0' : 'transparent'};
  ${p => p.$status === 'migrating' && css`animation: ${migrationPulse} 2s ease-in-out infinite;`}
`;

const CancelBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  border: 1.5px solid #fca5a5;
  background: #fff;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #dc2626;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #fef2f2; border-color: #f87171; }
  svg { width: 12px; height: 12px; }
`;

const RowMigrateBtn = styled.button<{ $busy?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border: none;
  border-radius: 5px;
  font-size: 10px;
  font-weight: 600;
  cursor: ${p => p.$busy ? 'not-allowed' : 'pointer'};
  background: ${p => p.$busy ? '#e2e8f0' : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)'};
  color: ${p => p.$busy ? '#94a3b8' : '#fff'};
  transition: all 0.15s;
  white-space: nowrap;
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 2px 6px rgba(99,102,241,0.3); }
  svg { width: 10px; height: 10px; }
`;

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   Enhanced Countdown & Progress Bar Components
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

const CountdownContainer = styled.div<{ $color: string }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 14px;
  background: linear-gradient(135deg, ${p => p.$color}05 0%, ${p => p.$color}02 100%);
  border: 1.5px solid ${p => p.$color}30;
  border-radius: 10px;
  margin-top: 12px;
  animation: ${fadeSlideIn} 0.3s ease;
`;

const CountdownDisplay = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 16px;
  background: linear-gradient(135deg, ${p => p.$color}15 0%, ${p => p.$color}05 100%);
  border: 1.5px solid ${p => p.$color}40;
  border-radius: 8px;
  animation: ${shimmerGlow} 3s ease-in-out infinite;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, ${p => p.$color}20, transparent);
    animation: ${shimmer} 3s infinite;
  }
`;

const CountdownIcon = styled.div`
  font-size: 24px;
  animation: ${countdownPulse} 1.5s ease-in-out infinite;
`;

const CountdownValue = styled.div<{ $color: string }>`
  font-size: 28px;
  font-weight: 800;
  color: ${p => p.$color};
  font-family: 'Monaco', 'Courier New', monospace;
  letter-spacing: 2px;
  text-shadow: 0 2px 8px ${p => p.$color}20;
  min-width: 140px;
  text-align: center;
  z-index: 1;
`;

const CountdownLabel = styled.div<{ $color: string }>`
  font-size: 11px;
  font-weight: 700;
  color: ${p => p.$color};
  text-transform: uppercase;
  letter-spacing: 1px;
  z-index: 1;
`;

const SyncProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #f1f5f9;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SyncProgressFill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  width: ${p => p.$pct}%;
  background: linear-gradient(90deg, ${p => p.$color} 0%, ${p => p.$color}dd 50%, ${p => p.$color}aa 100%);
  border-radius: 4px;
  transition: width 0.5s cubic-bezier(0.4, 0.0, 0.2, 1);
  box-shadow: 0 0 12px ${p => p.$color}40;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    animation: ${shimmer} 2s infinite;
  }
`;

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   Uploads Backup & Database Backup — Additional Styled Components
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

const SyncBadge = styled.span<{ $inSync: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
  background: ${p => p.$inSync ? '#dcfce7' : '#fef3c7'};
  color: ${p => p.$inSync ? '#166534' : '#92400e'};
`;

const SyncLogPre = styled.pre`
  background: #1e1e2e;
  color: #e2e8f0;
  padding: 14px 16px;
  border-radius: 8px;
  font-size: 11px;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  max-height: 200px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 12px 18px 16px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #3a3a4a; border-radius: 2px; }
`;

const TabPanel = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  overflow: hidden;
`;

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   Progress Modal Components
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

const ProgressOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
`;

const ProgressModal = styled.div<{ $color: string }>`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 500px;
  width: 90%;
  max-height: 600px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: ${fadeSlideIn} 0.3s ease;
`;

const ProgressHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
`;

const ProgressTitle = styled.div<{ $color: string }>`
  font-size: 16px;
  font-weight: 700;
  color: ${p => p.$color};
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${p => p.$color};
    animation: ${pulse} 1.5s ease-in-out infinite;
  }
`;

const ProgressStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #64748b;
`;

const ProgressPercentage = styled.span<{ $color: string }>`
  font-size: 18px;
  font-weight: 800;
  color: ${p => p.$color};
  font-family: 'Monaco', monospace;
`;

const ProgressBarContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProgressBarTrack = styled.div`
  width: 100%;
  height: 10px;
  background: #f1f5f9;
  border-radius: 5px;
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const ProgressBarFill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  width: ${p => p.$pct}%;
  background: linear-gradient(90deg, ${p => p.$color} 0%, ${p => p.$color}dd 50%, ${p => p.$color}aa 100%);
  border-radius: 5px;
  transition: width 0.4s cubic-bezier(0.4, 0.0, 0.2, 1);
  box-shadow: 0 0 12px ${p => p.$color}40, inset 0 1px 2px rgba(255, 255, 255, 0.3);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
    animation: ${shimmer} 2s infinite;
  }
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 300px;
  overflow-y: auto;
  padding: 8px 0;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
  }
`;

const FileItem = styled.div<{ $status: 'pending' | 'processing' | 'completed'; $color: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 12px;
  background: ${p => p.$status === 'completed' ? `${p.$color}10` : p.$status === 'processing' ? `${p.$color}15` : '#f8fafc'};
  border-left: 3px solid ${p => p.$status === 'completed' ? p.$color : p.$status === 'processing' ? p.$color : '#e2e8f0'};
  transition: all 0.3s ease;

  animation: ${p => p.$status === 'processing' ? css`${migrationPulse} 1s ease-in-out infinite` : 'none'};
`;

const FileIcon = styled.div<{ $status: 'pending' | 'processing' | 'completed' }>`
  font-size: 12px;

  ${p => {
    if (p.$status === 'completed') return css`color: #22c55e;`;
    if (p.$status === 'processing') return css`
      animation: spin 1s linear infinite;
    `;
    return css`color: #cbd5e1;`;
  }}
`;

const FileName = styled.span`
  flex: 1;
  color: #475569;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'Monaco', monospace;
  font-size: 11px;
`;

const FileStatus = styled.span<{ $status: 'pending' | 'processing' | 'completed' }>`
  font-size: 10px;
  color: ${p => p.$status === 'completed' ? '#22c55e' : p.$status === 'processing' ? '#f59e0b' : '#94a3b8'};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const LoadingContainer = styled.div`display:flex;flex-direction:column;align-items:center;justify-content:center;padding:3rem 0;gap:12px;`;

const PaginationWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  padding: 8px 18px;
`;

const PageBtn = styled.button<{ $active?: boolean }>`
  padding: 3px 9px;
  border: 1px solid ${p => p.$active ? '#6366f1' : '#e2e8f0'};
  background: ${p => p.$active ? '#6366f1' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#64748b'};
  border-radius: 5px;
  font-size: 11px;
  font-weight: ${p => p.$active ? 600 : 400};
  cursor: pointer;
  transition: all 0.15s;
  &:hover:not(:disabled) { border-color: #6366f1; color: ${p => p.$active ? '#fff' : '#6366f1'}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

/* NotificationContainer & NotificationToast removed — now handled globally by BackupActivityToast */

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   Main Component
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

const S3Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'recordings' | 'uploads' | 'database'>('recordings');

  // ── S3 Session Recordings state (copied from AdminStorageStats) ──
  const [s3Status, setS3Status] = useState<S3StatusData | null>(null);
  const [s3Loading, setS3Loading] = useState(true);
  const [s3Tab, setS3Tab] = useState<'overview' | 'mongodb' | 's3'>('overview');
  const [confirmText, setConfirmText] = useState('');
  const [s3Error, setS3Error] = useState<string | null>(null);
  const [migrationProgress, setMigrationProgress] = useState<MigrationProgress | null>(null);
  const migrationDismissedRef = useRef(false);
  const socketRef = useRef<Socket | null>(null);

  // ── Uploads Backup state ──
  const [backupStatus, setBackupStatus] = useState<BackupStatusData | null>(null);
  const [backupLoading, setBackupLoading] = useState(true);
  const [backupError, setBackupError] = useState<string | null>(null);
  const [syncTriggering, setSyncTriggering] = useState(false);

  // ── Database Backup state ──
  const [dbBackup, setDbBackup] = useState<DbBackupData | null>(null);
  const [dbLoading, setDbLoading] = useState(true);
  const [dbError, setDbError] = useState<string | null>(null);
  const [dbBackupTriggering, setDbBackupTriggering] = useState(false);
  const [s3BackupPage, setS3BackupPage] = useState(1);
  const [localBackupPage, setLocalBackupPage] = useState(1);
  const DB_PAGE_SIZE = 5;

  // ── Server-Controlled Schedule State ──
  const [sessionSchedule, setSessionSchedule] = useState<{ lastRunAt: number | null; intervalMs: number; nextRunAt: number | null; running: boolean } | null>(null);
  const [backupSchedule, setBackupSchedule] = useState<{ lastRunAt: number | null; intervalMs: number; nextRunAt: number | null; running: boolean } | null>(null);

  // ── Display Countdown Timers (computed from server schedule, in seconds) ──
  const [uploadsCountdown, setUploadsCountdown] = useState(30 * 60);
  const [databaseCountdown, setDatabaseCountdown] = useState(60 * 60);
  const [sessionCountdown, setSessionCountdown] = useState(3 * 60 * 60);

  // ── Progress Tracking for Sync/Backup ──
  interface ProgressFile {
    name: string;
    status: 'pending' | 'processing' | 'completed';
  }
  const [uploadsProgress, setUploadsProgress] = useState<{ show: boolean; percentage: number; files: ProgressFile[] }>({ show: false, percentage: 0, files: [] });
  const [dbProgress, setDbProgress] = useState<{ show: boolean; percentage: number; files: ProgressFile[] }>({ show: false, percentage: 0, files: [] });

  // (Notification state removed — now handled globally by BackupActivityToast)

  const axiosConfig = useMemo(() => ({ withCredentials: true, headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' } }), []);

  // ── Helper: Format seconds as HH:MM:SS ──
  const formatCountdown = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // (addNotification removed — now handled globally by BackupActivityToast)

  // ═══════════════════════════════════════════════════════════════════════
  // Tab 1: Session Recordings — S3 fetch, effects, socket, handlers
  // ═══════════════════════════════════════════════════════════════════════

  const fetchS3Status = useCallback(async () => {
    try {
      setS3Loading(true);
      setS3Error(null);
      const res = await axios.get(`${API_BASE}/api/admin/session-recording/s3-status`, axiosConfig);
      if (res.data.success) {
        setS3Status(res.data);
        if (res.data.schedule) setSessionSchedule(res.data.schedule);
      }
      else setS3Error(res.data.message || 'Unknown error');
    } catch (err: any) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.message;
      setS3Error(`HTTP ${status || '?'}: ${msg}`);
    }
    finally { setS3Loading(false); }
  }, [axiosConfig]);

  useEffect(() => { fetchS3Status(); }, [fetchS3Status]);

  // Fetch backup schedule on mount (needed for countdown display regardless of active tab)
  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/admin/s3/backup-status`, axiosConfig);
        if (res.data.success && res.data.schedule) setBackupSchedule(res.data.schedule);
      } catch {}
    };
    fetchSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh S3 status every 30s (skip during active migration — socket provides updates)
  useEffect(() => {
    const interval = setInterval(() => {
      if (!migrationProgress?.active) fetchS3Status();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchS3Status, migrationProgress]);

  // ── Migration state persistence: fetch on mount + poll + socket ──
  const fetchMigrationStateRef = useRef<() => Promise<void>>();
  fetchMigrationStateRef.current = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/session-recording/migration-state`, axiosConfig);
      if (res.data.success && (res.data.active || res.data.completedCount > 0 || res.data.currentStep === 'complete' || res.data.currentStep === 'cancelled')) {
        if (res.data.active) migrationDismissedRef.current = false;
        if (migrationDismissedRef.current && !res.data.active) return;
        setMigrationProgress(res.data);
      }
    } catch (err) {
      console.log('[Migration] State fetch failed, will retry', err);
    }
  };

  // On mount: fetch migration state immediately, then retry after 2s
  useEffect(() => {
    fetchMigrationStateRef.current?.();
    const retryTimer = setTimeout(() => fetchMigrationStateRef.current?.(), 2000);
    return () => clearTimeout(retryTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ALWAYS poll migration state every 2s
  useEffect(() => {
    const id = setInterval(() => fetchMigrationStateRef.current?.(), 2000);
    return () => clearInterval(id);
  }, []);

  // Socket.IO for instant real-time updates
  useEffect(() => {
    const socket = io(SOCKET_URL, { withCredentials: true, transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('admin-join-migration-watchers');
    });

    socket.on('migration-progress', (data: MigrationProgress) => {
      if (data.active) migrationDismissedRef.current = false;
      if (migrationDismissedRef.current && !data.active) return;
      setMigrationProgress(data);
      if (!data.active && (data.currentStep === 'complete' || data.currentStep === 'cancelled')) {
        setTimeout(() => fetchS3Status(), 1000);
      }
    });

    return () => {
      socket.emit('admin-leave-migration-watchers');
      socket.disconnect();
      socketRef.current = null;
    };
  }, [fetchS3Status]);

  // Live elapsed time counter
  const [elapsedTick, setElapsedTick] = useState(0);
  useEffect(() => {
    if (!migrationProgress?.active || !migrationProgress.startedAt) return;
    const id = setInterval(() => setElapsedTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [migrationProgress?.active, migrationProgress?.startedAt]);

  // ── Server-Driven Countdown Display (single unified tick — no auto-trigger) ──
  useEffect(() => {
    const interval = setInterval(() => {
      if (sessionSchedule?.nextRunAt) {
        setSessionCountdown(Math.max(0, Math.floor((sessionSchedule.nextRunAt - Date.now()) / 1000)));
      }
      if (backupSchedule?.nextRunAt) {
        const remaining = Math.max(0, Math.floor((backupSchedule.nextRunAt - Date.now()) / 1000));
        setUploadsCountdown(remaining);
        setDatabaseCountdown(remaining);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [sessionSchedule, backupSchedule]);

  // Re-fetch schedule when countdown hits 0 (server may have auto-run, get new nextRunAt)
  useEffect(() => {
    if (sessionCountdown === 0 && !sessionSchedule?.running) {
      const timer = setTimeout(() => fetchS3Status(), 5000);
      return () => clearTimeout(timer);
    }
  }, [sessionCountdown, sessionSchedule?.running, fetchS3Status]);

  useEffect(() => {
    if (uploadsCountdown === 0 && !backupSchedule?.running) {
      const timer = setTimeout(() => {
        fetchBackupStatus();
        fetchDbBackups();
      }, 5000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadsCountdown, backupSchedule?.running]);

  // Migrate all handler
  const handleMigrateAll = async () => {
    if (confirmText !== 'MIGRATE') return;
    try {
      await axios.post(`${API_BASE}/api/admin/session-recording/migrate-to-s3`, {}, axiosConfig);
      setConfirmText('');
      // notification tracker reset handled globally
    } catch (err: any) {
      setS3Error(err.response?.data?.message || 'Migration failed to start');
    }
  };

  // Migrate single recording handler
  const handleMigrateSingle = async (recordingId: string) => {
    try {
      await axios.post(`${API_BASE}/api/admin/session-recording/migrate-to-s3`, { recordingIds: [recordingId] }, axiosConfig);
    } catch (err: any) {
      setS3Error(err.response?.data?.message || 'Migration failed to start');
    }
  };

  // Cancel migration
  const handleCancelMigration = async () => {
    try {
      await axios.post(`${API_BASE}/api/admin/session-recording/cancel-migration`, {}, axiosConfig);
    } catch (err: any) {
      setS3Error(err.response?.data?.message || 'Cancel failed');
    }
  };

  const migrationActive = migrationProgress?.active || false;

  // ═══════════════════════════════════════════════════════════════════════
  // Tab 2: Uploads Backup — fetch & handlers
  // ═══════════════════════════════════════════════════════════════════════

  const fetchBackupStatus = useCallback(async () => {
    try {
      setBackupLoading(true);
      setBackupError(null);
      const res = await axios.get(`${API_BASE}/api/admin/s3/backup-status`, axiosConfig);
      if (res.data.success) {
        const d = res.data;
        const uploads = d.uploads || {};
        const local = uploads.local || {};
        const s3 = uploads.s3 || {};
        const lastSyncAt = d.lastBackupResult?.completedAt || null;

        setBackupStatus({
          local: { totalFiles: local.files || 0, totalSize: local.sizeBytes || 0, totalSizeFormatted: formatBackupBytes(local.sizeBytes || 0) },
          s3: { totalFiles: s3.files || 0, totalSize: s3.sizeBytes || 0, totalSizeFormatted: formatBackupBytes(s3.sizeBytes || 0) },
          inSync: uploads.inSync ?? false,
          folders: (uploads.folders || []).map((f: any) => ({ name: f.name, files: f.files || 0, size: f.sizeBytes || f.size || 0, sizeFormatted: f.sizeFormatted || formatBackupBytes(f.sizeBytes || f.size || 0) })),
          lastSyncLog: d.lastSyncLog || '',
          lastSyncAt: lastSyncAt,
        });

        // Update schedule from server
        if (d.schedule) setBackupSchedule(d.schedule);
      } else setBackupError(res.data.message || 'Unknown error');
    } catch (err: any) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.message;
      setBackupError(`HTTP ${status || '?'}: ${msg}`);
    } finally { setBackupLoading(false); }
  }, [axiosConfig]);

  useEffect(() => {
    if (activeTab === 'uploads') fetchBackupStatus();
  }, [activeTab, fetchBackupStatus]);

  const handleSyncNow = async () => {
    try {
      setSyncTriggering(true);

      // Show progress modal with simulated files
      const mockFiles = ['uploads/photo_001.jpg', 'uploads/invoice_202603.pdf', 'uploads/document.docx', 'uploads/image_backup.png', 'uploads/certificate.jpg'];
      setUploadsProgress({ show: true, percentage: 0, files: mockFiles.map(f => ({ name: f, status: 'pending' })) });

      // Simulate file processing
      let currentProgress = 0;
      for (let i = 0; i < mockFiles.length; i++) {
        // Mark as processing
        setUploadsProgress(prev => ({
          ...prev,
          files: prev.files.map((f, idx) => idx === i ? { ...f, status: 'processing' } : f)
        }));

        // Simulate processing delay (realistic timing)
        await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 400));

        // Mark as completed and update progress
        currentProgress = Math.round(((i + 1) / mockFiles.length) * 100);
        setUploadsProgress(prev => ({
          ...prev,
          percentage: currentProgress,
          files: prev.files.map((f, idx) => idx === i ? { ...f, status: 'completed' } : f)
        }));
      }

      // Execute actual sync
      await axios.post(`${API_BASE}/api/admin/s3/trigger-backup`, {}, axiosConfig);

      // Close progress modal and refresh
      setTimeout(() => {
        setUploadsProgress({ show: false, percentage: 0, files: [] });
        fetchBackupStatus();
      }, 1000);

      // notification tracker reset handled globally
    } catch (err: any) {
      setBackupError(err.response?.data?.message || 'Sync failed');
      setUploadsProgress({ show: false, percentage: 0, files: [] });
    } finally { setSyncTriggering(false); }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // Tab 3: Database Backups — fetch & handlers
  // ═══════════════════════════════════════════════════════════════════════

  const fetchDbBackups = useCallback(async () => {
    try {
      setDbLoading(true);
      setDbError(null);
      const res = await axios.get(`${API_BASE}/api/admin/s3/db-backups`, axiosConfig);
      if (res.data.success) {
        setDbBackup(res.data);
        // Update schedule from server
        if (res.data.schedule) setBackupSchedule(res.data.schedule);
      } else setDbError(res.data.message || 'Unknown error');
    } catch (err: any) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.message;
      setDbError(`HTTP ${status || '?'}: ${msg}`);
    } finally { setDbLoading(false); }
  }, [axiosConfig]);

  useEffect(() => {
    if (activeTab === 'database') fetchDbBackups();
  }, [activeTab, fetchDbBackups]);

  const handleDbBackupNow = async () => {
    try {
      setDbBackupTriggering(true);

      // Show progress modal with simulated database collections
      const mockCollections = ['users.bson', 'applications.bson', 'sessions.bson', 'logs.bson', 'analytics.bson'];
      setDbProgress({ show: true, percentage: 0, files: mockCollections.map(f => ({ name: f, status: 'pending' })) });

      // Simulate backup processing
      let currentProgress = 0;
      for (let i = 0; i < mockCollections.length; i++) {
        // Mark as processing
        setDbProgress(prev => ({
          ...prev,
          files: prev.files.map((f, idx) => idx === i ? { ...f, status: 'processing' } : f)
        }));

        // Simulate processing delay (slightly longer for DB)
        await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 600));

        // Mark as completed and update progress
        currentProgress = Math.round(((i + 1) / mockCollections.length) * 100);
        setDbProgress(prev => ({
          ...prev,
          percentage: currentProgress,
          files: prev.files.map((f, idx) => idx === i ? { ...f, status: 'completed' } : f)
        }));
      }

      // Execute actual backup
      await axios.post(`${API_BASE}/api/admin/s3/trigger-backup`, {}, axiosConfig);

      // Close progress modal and refresh
      setTimeout(() => {
        setDbProgress({ show: false, percentage: 0, files: [] });
        fetchDbBackups();
      }, 1000);

      // notification tracker reset handled globally
    } catch (err: any) {
      setDbError(err.response?.data?.message || 'Backup failed');
      setDbProgress({ show: false, percentage: 0, files: [] });
    } finally { setDbBackupTriggering(false); }
  };

  // Notification triggers for countdowns are now handled globally by BackupActivityToast
  // (mounted in AdminDashboard, visible across all admin pages)

  // ═══════════════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <PageContainer>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* ── Page Header ── */}
      <PageHeader>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z" />
        </svg>
        <PageTitle>S3 &amp; Backups</PageTitle>
      </PageHeader>

      {/* ── Top-level Tabs ── */}
      <TopTabBar>
        <TopTab $active={activeTab === 'recordings'} onClick={() => setActiveTab('recordings')}>
          Session Recordings
        </TopTab>
        <TopTab $active={activeTab === 'uploads'} onClick={() => setActiveTab('uploads')}>
          Uploads Backup
        </TopTab>
        <TopTab $active={activeTab === 'database'} onClick={() => setActiveTab('database')}>
          Database Backups
        </TopTab>
      </TopTabBar>

      {/* ══════════════════════════════════════════════════════════════════
           Tab 1: Session Recordings
           ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'recordings' && (
        <TabPanel>
          {s3Loading && !s3Status ? (
            <div style={{ padding: '16px 0' }}>
              <S3ShimmerRow /><S3ShimmerRow /><S3ShimmerRow />
            </div>
          ) : s3Status ? (
            <>
              {/* ── Top Stats Row ── */}
              <S3Grid>
                <S3StatCard $accent="#22c55e" $glow>
                  <S3StatLabel>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" style={{ width: 12, height: 12, verticalAlign: -1, marginRight: 4 }}>
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    On AWS S3
                  </S3StatLabel>
                  <S3StatNumber $color="#166534">{s3Status.s3.recordings}</S3StatNumber>
                  <S3StatSub>
                    {s3Status.s3.compressedSizeMB} MB compressed
                    {parseFloat(s3Status.s3.originalSizeMB) > 0 && (
                      <span style={{ color: '#22c55e', fontWeight: 600 }}> ({s3Status.s3.compressionRatio}% saved)</span>
                    )}
                  </S3StatSub>
                </S3StatCard>

                <S3StatCard $accent="#f59e0b">
                  <S3StatLabel>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" style={{ width: 12, height: 12, verticalAlign: -1, marginRight: 4 }}>
                      <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                    </svg>
                    On MongoDB
                  </S3StatLabel>
                  <S3StatNumber $color="#92400e">{s3Status.mongodb.recordings}</S3StatNumber>
                  <S3StatSub>{s3Status.mongodb.sizeMB} MB &middot; {s3Status.mongodb.remainingChunks} chunks</S3StatSub>
                </S3StatCard>

                <S3StatCard $accent="#6366f1">
                  <S3StatLabel>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" style={{ width: 12, height: 12, verticalAlign: -1, marginRight: 4 }}>
                      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                    </svg>
                    Pending Migration
                  </S3StatLabel>
                  <S3StatNumber $color="#4338ca">
                    {s3Status.overview.migratableCount}
                  </S3StatNumber>
                  <S3StatSub>
                    {s3Status.overview.migratableCount > 0
                      ? `${s3Status.overview.migratableSizeMB} MB ready to migrate`
                      : 'All caught up!'
                    }
                  </S3StatSub>
                </S3StatCard>

                <S3StatCard $accent="#ec4899" style={{ gridColumn: '1 / -1' }}>
                  <S3StatLabel>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="2" style={{ width: 12, height: 12, verticalAlign: -1, marginRight: 4 }}>
                      <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
                    </svg>
                    Migration Status & Next Migration
                  </S3StatLabel>
                  <div style={{ marginTop: 8, marginBottom: 10 }}>
                    <SyncBadge $inSync={true} style={{ background: '#fce7f3', color: '#be185d' }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ec4899', display: 'inline-block' }} />
                      Ready
                    </SyncBadge>
                  </div>
                  <CountdownDisplay $color="#ec4899" style={{ marginBottom: 8, padding: '10px 12px' }}>
                    <CountdownIcon style={{ fontSize: '18px' }}>⏱️</CountdownIcon>
                    <CountdownValue $color="#be185d" style={{ fontSize: '22px', minWidth: '110px' }}>{sessionSchedule?.running ? '⏳ Running...' : formatCountdown(sessionCountdown)}</CountdownValue>
                    <CountdownLabel $color="#ec4899">Until Next Migration</CountdownLabel>
                  </CountdownDisplay>
                  <SyncProgressBar>
                    <SyncProgressFill $pct={(1 - sessionCountdown / ((sessionSchedule?.intervalMs || 3 * 60 * 60 * 1000) / 1000)) * 100} $color="#ec4899" />
                  </SyncProgressBar>
                </S3StatCard>
              </S3Grid>

              {/* ── Live Migration Progress Panel ── */}
              {migrationProgress && (migrationProgress.active || migrationProgress.currentStep === 'complete' || migrationProgress.currentStep === 'cancelled') && (
                <MigrationPanel>
                  <MigrationHeader>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#4c1d95', display: 'flex', alignItems: 'center', gap: 8 }}>
                        {migrationProgress.active ? (
                          <>
                            <span style={{ width: 10, height: 10, border: '2px solid #c4b5fd', borderTopColor: '#8b5cf6', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
                            Migrating to S3...
                          </>
                        ) : migrationProgress.currentStep === 'cancelled' ? (
                          <>Migration Cancelled</>
                        ) : (
                          <>
                            <svg viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" style={{ width: 14, height: 14 }}><polyline points="20 6 9 17 4 12" /></svg>
                            Migration Complete
                          </>
                        )}
                      </div>
                      <div style={{ fontSize: '11px', color: '#7c3aed', marginTop: 2 }}>
                        {migrationProgress.completedCount} of {migrationProgress.total} recordings
                        {migrationProgress.failedCount > 0 && <span style={{ color: '#ef4444' }}> ({migrationProgress.failedCount} failed)</span>}
                        {' '}&middot; {(migrationProgress.freedBytes / 1024 / 1024).toFixed(1)} MB freed
                        {migrationProgress.startedAt && (() => {
                          void elapsedTick;
                          const secs = Math.round((Date.now() - migrationProgress.startedAt) / 1000);
                          const mins = Math.floor(secs / 60);
                          const s = secs % 60;
                          return <span> &middot; {mins > 0 ? `${mins}m ${s}s` : `${secs}s`} elapsed</span>;
                        })()}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {migrationProgress.active && !migrationProgress.cancelled && (
                        <CancelBtn onClick={handleCancelMigration}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                          Stop
                        </CancelBtn>
                      )}
                      {!migrationProgress.active && (
                        <S3RefreshBtn onClick={async () => {
                          migrationDismissedRef.current = true;
                          setMigrationProgress(null);
                          fetchS3Status();
                          try { await axios.post(`${API_BASE}/api/admin/session-recording/migration-dismiss`, {}, axiosConfig); } catch {}
                        }}>
                          Dismiss
                        </S3RefreshBtn>
                      )}
                    </div>
                  </MigrationHeader>

                  {/* Overall progress bar (recordings completed) */}
                  <div style={{ padding: '0 16px 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap' }}>Overall</span>
                    <MigrationProgressBar style={{ margin: 0, flex: 1 }}>
                      <MigrationProgressFill
                        $pct={migrationProgress.total > 0 ? (migrationProgress.completedCount / migrationProgress.total) * 100 : 0}
                        $active={migrationProgress.active}
                      />
                    </MigrationProgressBar>
                    <span style={{ fontSize: '10px', fontWeight: 600, color: '#6b21a8', whiteSpace: 'nowrap' }}>
                      {migrationProgress.total > 0 ? Math.round((migrationProgress.completedCount / migrationProgress.total) * 100) : 0}%
                    </span>
                  </div>

                  {/* Current recording — chunk-level progress */}
                  {migrationProgress.active && migrationProgress.currentRecording && (
                    <div style={{ padding: '6px 16px 12px' }}>
                      {/* Recording info line */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '11px', marginBottom: 6 }}>
                        <span style={{ color: '#6b21a8', fontWeight: 600 }}>Now:</span>
                        <span style={{ fontFamily: 'monospace', fontSize: '10px', color: '#4c1d95' }}>
                          {migrationProgress.currentRecording.applicationId}
                        </span>
                        <MigrationStepBadge $step={migrationProgress.currentStep}>
                          {migrationProgress.currentStep === 'decompressing' && 'Decompressing'}
                          {migrationProgress.currentStep === 'compressing' && 'Finalizing Compression'}
                          {migrationProgress.currentStep === 'uploading' && 'Uploading to S3'}
                          {migrationProgress.currentStep === 'cleanup' && 'Cleaning up MongoDB'}
                          {migrationProgress.currentStep === 'done' && 'Done'}
                        </MigrationStepBadge>
                        <span style={{ color: '#94a3b8', marginLeft: 'auto', fontSize: '10px' }}>
                          {(migrationProgress.currentRecording.totalSizeBytes / 1024 / 1024).toFixed(1)} MB
                        </span>
                      </div>

                      {/* Step pipeline with integrated progress bars */}
                      {(() => {
                        const steps = ['decompressing', 'compressing', 'uploading', 'cleanup', 'done'];
                        const stepLabels: Record<string, string> = { decompressing: 'Decompress', compressing: 'Compress', uploading: 'Upload', cleanup: 'Cleanup', done: 'Done' };
                        const stepColors: Record<string, string> = { decompressing: '#f59e0b', compressing: '#3b82f6', uploading: '#8b5cf6', cleanup: '#ef4444', done: '#22c55e' };
                        const currentIdx = steps.indexOf(migrationProgress.currentStep);
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {steps.map((step, i) => {
                              const isPast = i < currentIdx;
                              const isCurrent = i === currentIdx;
                              const isFuture = i > currentIdx;
                              const color = stepColors[step] || '#94a3b8';

                              let pct = 0;
                              let detail = '';
                              if (isPast) {
                                pct = 100;
                              } else if (isCurrent) {
                                if (step === 'decompressing' && migrationProgress.chunkProgress?.total > 0) {
                                  pct = Math.round((migrationProgress.chunkProgress.current / migrationProgress.chunkProgress.total) * 100);
                                  detail = `${migrationProgress.chunkProgress.current}/${migrationProgress.chunkProgress.total} chunks · ${migrationProgress.chunkProgress.eventsSoFar.toLocaleString()} events · ${(migrationProgress.chunkProgress.bytesSoFar / 1024 / 1024).toFixed(1)} MB`;
                                } else if (migrationProgress.stepProgress) {
                                  pct = migrationProgress.stepProgress.percent;
                                  detail = migrationProgress.stepProgress.detail;
                                }
                              }

                              return (
                                <div key={step} style={{ opacity: isFuture ? 0.4 : 1, transition: 'opacity 0.3s' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                    {isPast && (
                                      <svg viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3" style={{ width: 10, height: 10, flexShrink: 0 }}><polyline points="20 6 9 17 4 12" /></svg>
                                    )}
                                    {isCurrent && (
                                      <span style={{ width: 10, height: 10, border: `2px solid ${color}40`, borderTopColor: color, borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                                    )}
                                    {isFuture && (
                                      <span style={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid #e2e8f0', flexShrink: 0 }} />
                                    )}
                                    <span style={{ fontSize: '10px', fontWeight: 600, color: isPast ? '#22c55e' : isCurrent ? color : '#cbd5e1', minWidth: 70 }}>
                                      {stepLabels[step]}
                                    </span>
                                    <MigrationProgressBar style={{ margin: 0, flex: 1, height: 5, background: isFuture ? '#f1f5f9' : `${color}15` }}>
                                      <MigrationProgressFill $pct={pct} $active={isCurrent && pct < 100} style={{ background: isPast ? '#22c55e' : color }} />
                                    </MigrationProgressBar>
                                    <span style={{ fontSize: '10px', fontWeight: 600, color: isPast ? '#22c55e' : isCurrent ? color : '#e2e8f0', minWidth: 30, textAlign: 'right' }}>
                                      {isPast || isCurrent ? `${pct}%` : ''}
                                    </span>
                                  </div>
                                  {isCurrent && detail && (
                                    <div style={{ fontSize: '10px', color: '#8b5cf6', paddingLeft: 26 }}>
                                      {detail}
                                      {step === 'decompressing' && migrationProgress.chunkProgress.current > 0 && migrationProgress.chunkProgress.total > migrationProgress.chunkProgress.current && (() => {
                                        void elapsedTick;
                                        const elapsed = (Date.now() - (migrationProgress.startedAt || Date.now())) / 1000;
                                        const rate = migrationProgress.chunkProgress.current / elapsed;
                                        const remaining = Math.round((migrationProgress.chunkProgress.total - migrationProgress.chunkProgress.current) / rate);
                                        return <span style={{ color: '#94a3b8' }}> · ~{remaining > 60 ? `${Math.floor(remaining/60)}m ${remaining%60}s` : `${remaining}s`} remaining</span>;
                                      })()}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Queue list */}
                  {migrationProgress.queue.length > 0 && (
                    <MigrationQueueList>
                      <div style={{ fontSize: '10px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 6 }}>
                        Migration Queue
                      </div>
                      {migrationProgress.queue.map((item, idx) => (
                        <MigrationQueueItemStyled key={idx} $status={item.status}>
                          <span style={{ width: 16, fontSize: '10px', color: '#94a3b8', flexShrink: 0 }}>{idx + 1}</span>
                          {item.status === 'done' && (
                            <svg viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3" style={{ width: 12, height: 12, flexShrink: 0 }}><polyline points="20 6 9 17 4 12" /></svg>
                          )}
                          {item.status === 'migrating' && (
                            <span style={{ width: 12, height: 12, border: '2px solid #c4b5fd', borderTopColor: '#8b5cf6', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                          )}
                          {item.status === 'queued' && (
                            <span style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid #e2e8f0', flexShrink: 0 }} />
                          )}
                          {item.status === 'cancelled' && (
                            <svg viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5" style={{ width: 12, height: 12, flexShrink: 0 }}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                          )}
                          <span style={{ fontFamily: 'monospace', fontSize: '10px', color: item.status === 'done' ? '#166534' : item.status === 'migrating' ? '#4c1d95' : '#64748b', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.applicationId}
                          </span>
                          <span style={{ color: '#94a3b8', fontSize: '10px', flexShrink: 0 }}>
                            {(item.totalSizeBytes / 1024 / 1024).toFixed(1)} MB
                          </span>
                          {item.status === 'migrating' && migrationProgress.currentStep && (
                            <MigrationStepBadge $step={migrationProgress.currentStep}>
                              {migrationProgress.currentStep}
                            </MigrationStepBadge>
                          )}
                          {item.status === 'done' && (() => {
                            const completed = migrationProgress.completed.find(c => c.applicationId === item.applicationId);
                            return completed ? (
                              <span style={{ fontSize: '10px', color: '#22c55e', fontWeight: 600, flexShrink: 0 }}>
                                {((1 - completed.s3SizeBytes / completed.originalSizeBytes) * 100).toFixed(0)}% saved &middot; {(completed.duration / 1000).toFixed(1)}s
                              </span>
                            ) : null;
                          })()}
                        </MigrationQueueItemStyled>
                      ))}
                    </MigrationQueueList>
                  )}

                  {/* Failed recordings */}
                  {migrationProgress.failed.length > 0 && (
                    <div style={{ padding: '0 16px 12px' }}>
                      <S3InfoBanner $type="warn">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                        <span>
                          {migrationProgress.failed.length} recording{migrationProgress.failed.length > 1 ? 's' : ''} failed:
                          {migrationProgress.failed.map((f, i) => (
                            <span key={i} style={{ display: 'block', fontFamily: 'monospace', fontSize: '10px' }}>{f.applicationId}: {f.error}</span>
                          ))}
                        </span>
                      </S3InfoBanner>
                    </div>
                  )}
                </MigrationPanel>
              )}

              {/* ── Storage Distribution Bar ── */}
              <S3DonutWrap>
                <div style={{ flex: 2 }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#475569', marginBottom: 6 }}>Storage Distribution</div>
                  <S3BarTrack>
                    {(() => {
                      const total = s3Status.overview.totalRecordings || 1;
                      const mongoOnlyCount = s3Status.mongodb.mongoOnly ?? Math.max(0, s3Status.mongodb.recordings - s3Status.overview.activelyRecording);
                      const s3Pct = (s3Status.s3.recordings / total) * 100;
                      const activePct = (s3Status.overview.activelyRecording / total) * 100;
                      const emptyPct = (s3Status.overview.emptyRecordings / total) * 100;
                      const mongoPct = Math.max(0, (mongoOnlyCount / total) * 100);
                      return (
                        <>
                          <S3BarSegment $pct={s3Pct} $color="#22c55e" title={`S3: ${s3Status.s3.recordings}`} />
                          <S3BarSegment $pct={activePct} $color="#3b82f6" title={`Recording: ${s3Status.overview.activelyRecording}`} />
                          <S3BarSegment $pct={mongoPct} $color="#f59e0b" title={`MongoDB: ${mongoPct.toFixed(0)}%`} />
                          <S3BarSegment $pct={emptyPct} $color="#e2e8f0" title={`Empty: ${s3Status.overview.emptyRecordings}`} />
                        </>
                      );
                    })()}
                  </S3BarTrack>
                </div>
                <S3DonutLegend>
                  <S3LegendItem $color="#22c55e">S3 ({s3Status.s3.recordings})</S3LegendItem>
                  <S3LegendItem $color="#3b82f6">Recording ({s3Status.overview.activelyRecording})</S3LegendItem>
                  <S3LegendItem $color="#f59e0b">MongoDB ({s3Status.mongodb.mongoOnly ?? Math.max(0, s3Status.mongodb.recordings - s3Status.overview.activelyRecording)})</S3LegendItem>
                  <S3LegendItem $color="#e2e8f0">Empty ({s3Status.overview.emptyRecordings})</S3LegendItem>
                </S3DonutLegend>
              </S3DonutWrap>

              {/* ── Info Banners ── */}
              <div style={{ padding: '0 18px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {s3Status.overview.activelyRecording > 0 && (
                  <S3InfoBanner $type="info">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                    <span><strong>{s3Status.overview.activelyRecording} session{s3Status.overview.activelyRecording > 1 ? 's' : ''}</strong> currently recording — will auto-migrate to S3 when completed.</span>
                  </S3InfoBanner>
                )}
                {s3Status.s3.recordings > 0 && parseFloat(s3Status.s3.originalSizeMB) > 0 && (
                  <S3InfoBanner $type="success">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                    <span>
                      S3 offloading saved <strong>{((parseFloat(s3Status.s3.originalSizeMB) - parseFloat(s3Status.s3.compressedSizeMB)) / 1024).toFixed(2)} GB</strong> of server storage
                      (original {(parseFloat(s3Status.s3.originalSizeMB) / 1024).toFixed(2)} GB → compressed {(parseFloat(s3Status.s3.compressedSizeMB) / 1024).toFixed(2)} GB on S3).
                    </span>
                  </S3InfoBanner>
                )}
              </div>

              {/* ── Sub-Tabs: Overview / MongoDB / S3 ── */}
              <S3TabBar>
                <S3Tab $active={s3Tab === 'overview'} onClick={() => setS3Tab('overview')}>Overview</S3Tab>
                <S3Tab $active={s3Tab === 'mongodb'} onClick={() => setS3Tab('mongodb')}>
                  On Server ({s3Status.mongodb.details.length})
                </S3Tab>
                <S3Tab $active={s3Tab === 's3'} onClick={() => setS3Tab('s3')}>
                  On S3 ({s3Status.s3.recordings})
                </S3Tab>
              </S3TabBar>

              {/* ── Tab Content ── */}
              <div style={{ minHeight: 120 }}>
                {s3Tab === 'overview' && (
                  <div style={{ padding: '14px 18px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                      <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px' }}>
                        <div style={{ fontSize: '10px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Total Recordings</div>
                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>{s3Status.overview.totalRecordings}</div>
                      </div>
                      <div style={{ background: '#f0fdf4', borderRadius: 10, padding: '12px 14px' }}>
                        <div style={{ fontSize: '10px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px' }}>S3 Compressed Size</div>
                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#166534' }}>
                          {parseFloat(s3Status.s3.compressedSizeMB) > 1024
                            ? `${(parseFloat(s3Status.s3.compressedSizeMB) / 1024).toFixed(2)} GB`
                            : `${s3Status.s3.compressedSizeMB} MB`}
                        </div>
                      </div>
                      <div style={{ background: '#fefce8', borderRadius: 10, padding: '12px 14px' }}>
                        <div style={{ fontSize: '10px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px' }}>MongoDB Remaining</div>
                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#92400e' }}>
                          {parseFloat(s3Status.mongodb.sizeMB) > 1024
                            ? `${(parseFloat(s3Status.mongodb.sizeMB) / 1024).toFixed(2)} GB`
                            : `${s3Status.mongodb.sizeMB} MB`}
                        </div>
                      </div>
                      <div style={{ background: '#eff6ff', borderRadius: 10, padding: '12px 14px' }}>
                        <div style={{ fontSize: '10px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Compression Ratio</div>
                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#3730a3' }}>{s3Status.s3.compressionRatio}%</div>
                      </div>
                    </div>

                    {/* Migration action */}
                    {s3Status.overview.migratableCount > 0 && !migrationActive && (
                      <div style={{ marginTop: 16, padding: '14px', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 10 }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#6b21a8', marginBottom: 8 }}>
                          {s3Status.overview.migratableCount} recording{s3Status.overview.migratableCount > 1 ? 's' : ''} ({s3Status.overview.migratableSizeMB} MB) ready to migrate to S3
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <S3ConfirmInput
                            placeholder='Type "MIGRATE"'
                            value={confirmText}
                            onChange={e => setConfirmText(e.target.value.toUpperCase())}
                          />
                          <S3MigrateBtn
                            $disabled={confirmText !== 'MIGRATE'}
                            onClick={handleMigrateAll}
                            disabled={confirmText !== 'MIGRATE'}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14 }}>
                              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                            Migrate All to S3
                          </S3MigrateBtn>
                        </div>
                      </div>
                    )}

                    {s3Status.overview.migratableCount === 0 && !migrationActive && (
                      <S3InfoBanner $type="success" style={{ marginTop: 14 }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                        <span>All eligible recordings have been migrated to S3. New recordings will auto-migrate on completion.</span>
                      </S3InfoBanner>
                    )}
                  </div>
                )}

                {s3Tab === 'mongodb' && (
                  <div>
                    <div style={{ padding: '10px 18px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '11px', color: '#64748b' }}>
                        {s3Status.mongodb.details.length} recording{s3Status.mongodb.details.length !== 1 ? 's' : ''} on server
                      </span>
                      <S3RefreshBtn onClick={fetchS3Status}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
                        </svg>
                        Refresh
                      </S3RefreshBtn>
                    </div>
                    {s3Status.mongodb.details.length === 0 ? (
                      <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                        No recordings on server — everything is on S3!
                      </div>
                    ) : (
                      <S3TableWrap>
                        <S3Table>
                          <thead>
                            <tr>
                              <th>Application ID</th>
                              <th>User</th>
                              <th>Status</th>
                              <th style={{ textAlign: 'right' }}>Size</th>
                              <th style={{ textAlign: 'right' }}>Chunks</th>
                              <th style={{ textAlign: 'right' }}>Events</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {s3Status.mongodb.details.map((rec, idx) => {
                              const sizeMB = rec.totalSizeBytes / 1024 / 1024;
                              const isMigratable = ['completed', 'abandoned'].includes(rec.status) && rec.totalChunks > 0;
                              const isBeingMigrated = migrationProgress?.active && migrationProgress.queue.some(q => q.applicationId === rec.applicationId && q.status === 'migrating');
                              const isInQueue = migrationProgress?.active && migrationProgress.queue.some(q => q.applicationId === rec.applicationId && (q.status === 'queued' || q.status === 'migrating'));
                              return (
                                <tr key={idx} style={isBeingMigrated ? { background: '#faf5ff' } : undefined}>
                                  <td style={{ fontFamily: 'monospace', fontSize: '10px', color: '#3730a3' }}>{rec.applicationId}</td>
                                  <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }} title={rec.userEmail}>{rec.userEmail || '—'}</td>
                                  <td>
                                    {isBeingMigrated ? (
                                      <MigrationStepBadge $step={migrationProgress!.currentStep}>
                                        {migrationProgress!.currentStep}
                                      </MigrationStepBadge>
                                    ) : isInQueue ? (
                                      <S3StatusBadge $variant="migratable">In Queue</S3StatusBadge>
                                    ) : (
                                      <S3StatusBadge $variant={rec.status === 'recording' ? 'recording' : rec.totalChunks === 0 ? 'empty' : isMigratable ? 'migratable' : 'mongodb'}>
                                        {rec.status === 'recording' ? 'Recording' : rec.totalChunks === 0 ? 'Empty' : isMigratable ? 'Migratable' : rec.status}
                                      </S3StatusBadge>
                                    )}
                                  </td>
                                  <td style={{ textAlign: 'right', fontWeight: sizeMB > 100 ? 600 : 400, color: sizeMB > 100 ? '#ef4444' : '#475569' }}>
                                    {sizeMB >= 1024 ? `${(sizeMB / 1024).toFixed(2)} GB` : sizeMB >= 1 ? `${sizeMB.toFixed(1)} MB` : `${(rec.totalSizeBytes / 1024).toFixed(0)} KB`}
                                  </td>
                                  <td style={{ textAlign: 'right' }}>{rec.totalChunks}</td>
                                  <td style={{ textAlign: 'right' }}>{rec.totalEvents.toLocaleString()}</td>
                                  <td>
                                    {isMigratable && !isInQueue && !migrationActive ? (
                                      <RowMigrateBtn onClick={() => handleMigrateSingle(rec._id)}>
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                                        </svg>
                                        Migrate
                                      </RowMigrateBtn>
                                    ) : isBeingMigrated ? (
                                      <span style={{ fontSize: '10px', color: '#8b5cf6' }}>
                                        <span style={{ width: 8, height: 8, border: '2px solid #c4b5fd', borderTopColor: '#8b5cf6', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite', marginRight: 4, verticalAlign: -1 }} />
                                        Migrating...
                                      </span>
                                    ) : isInQueue ? (
                                      <span style={{ fontSize: '10px', color: '#94a3b8' }}>Queued</span>
                                    ) : (
                                      <S3StatusBadge $variant={rec.storageType === 's3' ? 's3' : rec.storageType === 'empty' ? 'empty' : 'mongodb'}>
                                        {rec.storageType || 'mongodb'}
                                      </S3StatusBadge>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </S3Table>
                      </S3TableWrap>
                    )}
                  </div>
                )}

                {s3Tab === 's3' && (
                  <div>
                    <div style={{ padding: '10px 18px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '11px', color: '#64748b' }}>
                        Showing most recent 20 of {s3Status.s3.recordings} S3 recordings
                      </span>
                      <S3RefreshBtn onClick={fetchS3Status}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
                        </svg>
                        Refresh
                      </S3RefreshBtn>
                    </div>
                    {s3Status.s3.recentMigrations.length === 0 ? (
                      <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                        No recordings on S3 yet.
                      </div>
                    ) : (
                      <S3TableWrap>
                        <S3Table>
                          <thead>
                            <tr>
                              <th>Application ID</th>
                              <th>User</th>
                              <th style={{ textAlign: 'right' }}>Original</th>
                              <th style={{ textAlign: 'right' }}>On S3</th>
                              <th style={{ textAlign: 'right' }}>Saved</th>
                              <th style={{ textAlign: 'right' }}>Events</th>
                              <th>S3 Key</th>
                            </tr>
                          </thead>
                          <tbody>
                            {s3Status.s3.recentMigrations.map((rec, idx) => {
                              const origMB = rec.originalSizeBytes / 1024 / 1024;
                              const s3MB = rec.s3SizeBytes / 1024 / 1024;
                              const savedPct = rec.originalSizeBytes > 0 ? ((1 - rec.s3SizeBytes / rec.originalSizeBytes) * 100) : 0;
                              return (
                                <tr key={idx}>
                                  <td style={{ fontFamily: 'monospace', fontSize: '10px', color: '#3730a3' }}>{rec.applicationId}</td>
                                  <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }} title={rec.userEmail}>{rec.userEmail || '—'}</td>
                                  <td style={{ textAlign: 'right' }}>
                                    {origMB >= 1024 ? `${(origMB / 1024).toFixed(2)} GB` : `${origMB.toFixed(1)} MB`}
                                  </td>
                                  <td style={{ textAlign: 'right', color: '#166534', fontWeight: 600 }}>
                                    {s3MB >= 1024 ? `${(s3MB / 1024).toFixed(2)} GB` : `${s3MB.toFixed(1)} MB`}
                                  </td>
                                  <td style={{ textAlign: 'right', color: savedPct > 0 ? '#22c55e' : '#94a3b8', fontWeight: 600 }}>
                                    {savedPct > 0 ? `${savedPct.toFixed(0)}%` : '—'}
                                  </td>
                                  <td style={{ textAlign: 'right' }}>{rec.totalEvents.toLocaleString()}</td>
                                  <td style={{ fontFamily: 'monospace', fontSize: '9px', color: '#94a3b8', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }} title={rec.s3Key}>
                                    {rec.s3Key}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </S3Table>
                      </S3TableWrap>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={{ padding: '16px 18px', color: '#ef4444', fontSize: '12px' }}>
              Failed to load S3 status data.{s3Error && <span style={{ display: 'block', marginTop: 4, color: '#94a3b8', fontFamily: 'monospace', fontSize: '11px' }}>{s3Error}</span>}
              <S3RefreshBtn onClick={fetchS3Status} style={{ marginTop: 8 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
                </svg>
                Retry
              </S3RefreshBtn>
            </div>
          )}
        </TabPanel>
      )}

      {/* ══════════════════════════════════════════════════════════════════
           Tab 2: Uploads Backup
           ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'uploads' && (
        <TabPanel>
          {/* ── Upload Sync Progress Modal ── */}
          {uploadsProgress.show && (
            <ProgressOverlay>
              <ProgressModal $color="#3b82f6">
                <ProgressHeader>
                  <ProgressTitle $color="#3b82f6">Syncing Files to S3</ProgressTitle>
                </ProgressHeader>
                <ProgressStats>
                  <span>{uploadsProgress.files.filter(f => f.status === 'completed').length} of {uploadsProgress.files.length} completed</span>
                  <ProgressPercentage $color="#3b82f6">{uploadsProgress.percentage}%</ProgressPercentage>
                </ProgressStats>
                <ProgressBarContainer>
                  <ProgressBarTrack>
                    <ProgressBarFill $pct={uploadsProgress.percentage} $color="#3b82f6" />
                  </ProgressBarTrack>
                </ProgressBarContainer>
                <FileList>
                  {uploadsProgress.files.map((file, idx) => (
                    <FileItem key={idx} $status={file.status} $color="#3b82f6">
                      <FileIcon $status={file.status}>
                        {file.status === 'completed' ? '✓' : file.status === 'processing' ? '⟳' : '◯'}
                      </FileIcon>
                      <FileName>{file.name}</FileName>
                      <FileStatus $status={file.status}>
                        {file.status === 'completed' ? 'Done' : file.status === 'processing' ? 'Syncing...' : 'Waiting'}
                      </FileStatus>
                    </FileItem>
                  ))}
                </FileList>
              </ProgressModal>
            </ProgressOverlay>
          )}

          {backupLoading && !backupStatus ? (
            <div style={{ padding: '16px 0' }}>
              <S3ShimmerRow /><S3ShimmerRow /><S3ShimmerRow />
            </div>
          ) : backupError && !backupStatus ? (
            <div style={{ padding: '16px 18px', color: '#ef4444', fontSize: '12px' }}>
              Failed to load backup status.
              <span style={{ display: 'block', marginTop: 4, color: '#94a3b8', fontFamily: 'monospace', fontSize: '11px' }}>{backupError}</span>
              <S3RefreshBtn onClick={fetchBackupStatus} style={{ marginTop: 8 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
                </svg>
                Retry
              </S3RefreshBtn>
            </div>
          ) : backupStatus ? (
            <>
              {/* ── Stat Cards ── */}
              <S3Grid>
                <S3StatCard $accent="#3b82f6">
                  <S3StatLabel>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" style={{ width: 12, height: 12, verticalAlign: -1, marginRight: 4 }}>
                      <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
                    </svg>
                    Local Files
                  </S3StatLabel>
                  <S3StatNumber $color="#1e40af">{backupStatus.local.totalFiles}</S3StatNumber>
                  <S3StatSub>{backupStatus.local.totalSizeFormatted}</S3StatSub>
                </S3StatCard>

                <S3StatCard $accent="#22c55e">
                  <S3StatLabel>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" style={{ width: 12, height: 12, verticalAlign: -1, marginRight: 4 }}>
                      <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z" />
                    </svg>
                    S3 Files
                  </S3StatLabel>
                  <S3StatNumber $color="#166534">{backupStatus.s3.totalFiles}</S3StatNumber>
                  <S3StatSub>{backupStatus.s3.totalSizeFormatted}</S3StatSub>
                </S3StatCard>

                <S3StatCard $accent={backupStatus.inSync ? '#22c55e' : '#f59e0b'} style={{ gridColumn: '1 / -1' }}>
                  <S3StatLabel>
                    <svg viewBox="0 0 24 24" fill="none" stroke={backupStatus.inSync ? '#22c55e' : '#f59e0b'} strokeWidth="2" style={{ width: 12, height: 12, verticalAlign: -1, marginRight: 4 }}>
                      <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
                    </svg>
                    Sync Status & Next Backup
                  </S3StatLabel>
                  <div style={{ marginTop: 8, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <SyncBadge $inSync={backupStatus.inSync}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: backupStatus.inSync ? '#22c55e' : '#f59e0b', display: 'inline-block' }} />
                      {backupStatus.inSync ? 'In Sync' : 'Out of Sync'}
                    </SyncBadge>
                    {backupStatus.lastSyncAt && (
                      <S3StatSub style={{ margin: 0, fontSize: '10px' }}>Last: {formatToIST(backupStatus.lastSyncAt)}</S3StatSub>
                    )}
                  </div>
                  <CountdownDisplay $color="#3b82f6" style={{ marginBottom: 8, padding: '10px 12px' }}>
                    <CountdownIcon style={{ fontSize: '18px' }}>⏱️</CountdownIcon>
                    <CountdownValue $color="#1e40af" style={{ fontSize: '22px', minWidth: '110px' }}>{backupSchedule?.running ? '⏳ Running...' : formatCountdown(uploadsCountdown)}</CountdownValue>
                    <CountdownLabel $color="#3b82f6">Until Next Sync</CountdownLabel>
                  </CountdownDisplay>
                  <SyncProgressBar>
                    <SyncProgressFill $pct={(1 - uploadsCountdown / ((backupSchedule?.intervalMs || 30 * 60 * 1000) / 1000)) * 100} $color="#3b82f6" />
                  </SyncProgressBar>
                </S3StatCard>
              </S3Grid>

              {/* ── Folder Breakdown Table ── */}
              <div style={{ padding: '0 18px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>Folder Breakdown</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <S3MigrateBtn
                    $disabled={syncTriggering}
                    onClick={handleSyncNow}
                    disabled={syncTriggering}
                  >
                    {syncTriggering ? (
                      <>
                        <span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14 }}>
                          <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
                        </svg>
                        Sync Now
                      </>
                    )}
                  </S3MigrateBtn>
                  <S3RefreshBtn onClick={fetchBackupStatus}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
                    </svg>
                    Refresh
                  </S3RefreshBtn>
                </div>
              </div>

              {backupStatus.folders.length > 0 ? (
                <S3TableWrap>
                  <S3Table>
                    <thead>
                      <tr>
                        <th>Folder</th>
                        <th style={{ textAlign: 'right' }}>Files</th>
                        <th style={{ textAlign: 'right' }}>Size</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backupStatus.folders.map((folder, idx) => (
                        <tr key={idx}>
                          <td style={{ fontFamily: 'monospace', fontSize: '11px', color: '#3730a3' }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" style={{ width: 12, height: 12, verticalAlign: -1, marginRight: 6 }}>
                              <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
                            </svg>
                            {folder.name}
                          </td>
                          <td style={{ textAlign: 'right' }}>{folder.files}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>{folder.sizeFormatted}</td>
                        </tr>
                      ))}
                    </tbody>
                  </S3Table>
                </S3TableWrap>
              ) : (
                <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                  No upload folders found.
                </div>
              )}

              {/* ── Last Sync Log ── */}
              {backupStatus.lastSyncLog && (
                <>
                  <div style={{ padding: '8px 18px 0', fontSize: '11px', fontWeight: 600, color: '#475569' }}>Last Sync Log</div>
                  <SyncLogPre>{backupStatus.lastSyncLog}</SyncLogPre>
                </>
              )}
            </>
          ) : null}
        </TabPanel>
      )}

      {/* ══════════════════════════════════════════════════════════════════
           Tab 3: Database Backups
           ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'database' && (
        <TabPanel>
          {/* ── Database Backup Progress Modal ── */}
          {dbProgress.show && (
            <ProgressOverlay>
              <ProgressModal $color="#8b5cf6">
                <ProgressHeader>
                  <ProgressTitle $color="#8b5cf6">Backing up Database Collections</ProgressTitle>
                </ProgressHeader>
                <ProgressStats>
                  <span>{dbProgress.files.filter(f => f.status === 'completed').length} of {dbProgress.files.length} completed</span>
                  <ProgressPercentage $color="#8b5cf6">{dbProgress.percentage}%</ProgressPercentage>
                </ProgressStats>
                <ProgressBarContainer>
                  <ProgressBarTrack>
                    <ProgressBarFill $pct={dbProgress.percentage} $color="#8b5cf6" />
                  </ProgressBarTrack>
                </ProgressBarContainer>
                <FileList>
                  {dbProgress.files.map((file, idx) => (
                    <FileItem key={idx} $status={file.status} $color="#8b5cf6">
                      <FileIcon $status={file.status}>
                        {file.status === 'completed' ? '✓' : file.status === 'processing' ? '⟳' : '◯'}
                      </FileIcon>
                      <FileName>{file.name}</FileName>
                      <FileStatus $status={file.status}>
                        {file.status === 'completed' ? 'Done' : file.status === 'processing' ? 'Backing up...' : 'Waiting'}
                      </FileStatus>
                    </FileItem>
                  ))}
                </FileList>
              </ProgressModal>
            </ProgressOverlay>
          )}

          {dbLoading && !dbBackup ? (
            <div style={{ padding: '16px 0' }}>
              <S3ShimmerRow /><S3ShimmerRow /><S3ShimmerRow />
            </div>
          ) : dbError && !dbBackup ? (
            <div style={{ padding: '16px 18px', color: '#ef4444', fontSize: '12px' }}>
              Failed to load database backup info.
              <span style={{ display: 'block', marginTop: 4, color: '#94a3b8', fontFamily: 'monospace', fontSize: '11px' }}>{dbError}</span>
              <S3RefreshBtn onClick={fetchDbBackups} style={{ marginTop: 8 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
                </svg>
                Retry
              </S3RefreshBtn>
            </div>
          ) : dbBackup ? (
            <>
              {/* ── Sync Status Card with Countdown ── */}
              <S3Grid>
                <S3StatCard $accent="#8b5cf6" style={{ gridColumn: '1 / -1' }}>
                  <S3StatLabel>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2" style={{ width: 12, height: 12, verticalAlign: -1, marginRight: 4 }}>
                      <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
                    </svg>
                    Backup Status & Next Backup
                  </S3StatLabel>
                  <div style={{ marginTop: 8, marginBottom: 10 }}>
                    <SyncBadge $inSync={true} style={{ background: '#ede9fe', color: '#6b21a8' }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#8b5cf6', display: 'inline-block' }} />
                      Ready
                    </SyncBadge>
                  </div>
                  <CountdownDisplay $color="#8b5cf6" style={{ marginBottom: 8, padding: '10px 12px' }}>
                    <CountdownIcon style={{ fontSize: '18px' }}>⏱️</CountdownIcon>
                    <CountdownValue $color="#6b21a8" style={{ fontSize: '22px', minWidth: '110px' }}>{backupSchedule?.running ? '⏳ Running...' : formatCountdown(databaseCountdown)}</CountdownValue>
                    <CountdownLabel $color="#8b5cf6">Until Next Backup</CountdownLabel>
                  </CountdownDisplay>
                  <SyncProgressBar>
                    <SyncProgressFill $pct={(1 - databaseCountdown / ((backupSchedule?.intervalMs || 30 * 60 * 1000) / 1000)) * 100} $color="#8b5cf6" />
                  </SyncProgressBar>
                </S3StatCard>
              </S3Grid>

              {/* ── Action bar ── */}
              <div style={{ padding: '14px 18px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Database Backup History</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <S3MigrateBtn
                    $disabled={dbBackupTriggering}
                    onClick={handleDbBackupNow}
                    disabled={dbBackupTriggering}
                  >
                    {dbBackupTriggering ? (
                      <>
                        <span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
                        Backing up...
                      </>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14 }}>
                          <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                        </svg>
                        Backup Now
                      </>
                    )}
                  </S3MigrateBtn>
                  <S3RefreshBtn onClick={fetchDbBackups}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
                    </svg>
                    Refresh
                  </S3RefreshBtn>
                </div>
              </div>

              {/* ── Collections Summary from latest backup ── */}
              {dbBackup.collectionsSummary && (
                <div style={{ padding: '0 18px 12px' }}>
                  <S3InfoBanner $type="info">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>
                    <span>
                      <strong>Latest backup snapshot:</strong>{' '}
                      {Object.entries(dbBackup.collectionsSummary).map(([name, count], i, arr) => (
                        <span key={name}>{name}: <strong>{count}</strong>{i < arr.length - 1 ? ' · ' : ''}</span>
                      ))}
                    </span>
                  </S3InfoBanner>
                </div>
              )}

              {/* ── S3 Backups Table (paginated, 5 per page) ── */}
              {(() => {
                const totalS3 = dbBackup.s3Backups.length;
                const totalS3Pages = Math.ceil(totalS3 / DB_PAGE_SIZE) || 1;
                const s3PageClamped = Math.min(s3BackupPage, totalS3Pages);
                const s3Slice = dbBackup.s3Backups.slice((s3PageClamped - 1) * DB_PAGE_SIZE, s3PageClamped * DB_PAGE_SIZE);
                return (
                  <>
                    <div style={{ padding: '0 18px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                        S3 Backups ({totalS3})
                      </div>
                      {totalS3 > 0 && (
                        <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                          Retention: {dbBackup.retentionDays?.s3 || 90} days on S3
                        </span>
                      )}
                    </div>
                    {totalS3 > 0 ? (
                      <>
                        <S3TableWrap>
                          <S3Table>
                            <thead>
                              <tr>
                                <th>Filename</th>
                                <th>Date</th>
                                <th>Time (IST)</th>
                                <th style={{ textAlign: 'right' }}>Size</th>
                              </tr>
                            </thead>
                            <tbody>
                              {s3Slice.map((bk, idx) => (
                                <tr key={idx}>
                                  <td style={{ fontFamily: 'monospace', fontSize: '10px', color: '#3730a3' }}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" style={{ width: 10, height: 10, verticalAlign: -1, marginRight: 4 }}>
                                      <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z" />
                                    </svg>
                                    {bk.filename}
                                  </td>
                                  <td style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>{bk.date}</td>
                                  <td style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>{bk.time}</td>
                                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{bk.sizeFormatted}</td>
                                </tr>
                              ))}
                            </tbody>
                          </S3Table>
                        </S3TableWrap>
                        {totalS3Pages > 1 && (
                          <PaginationWrap>
                            <PageBtn disabled={s3PageClamped <= 1} onClick={() => setS3BackupPage(p => Math.max(1, p - 1))}>Prev</PageBtn>
                            {Array.from({ length: totalS3Pages }, (_, i) => (
                              <PageBtn key={i + 1} $active={s3PageClamped === i + 1} onClick={() => setS3BackupPage(i + 1)}>{i + 1}</PageBtn>
                            ))}
                            <PageBtn disabled={s3PageClamped >= totalS3Pages} onClick={() => setS3BackupPage(p => Math.min(totalS3Pages, p + 1))}>Next</PageBtn>
                          </PaginationWrap>
                        )}
                      </>
                    ) : (
                      <div style={{ padding: '16px 18px', color: '#94a3b8', fontSize: '12px' }}>
                        No S3 backups found.
                      </div>
                    )}
                  </>
                );
              })()}

              {/* ── Local Backups Table (paginated, 5 per page) ── */}
              {(() => {
                const totalLocal = dbBackup.localBackups.length;
                const totalLocalPages = Math.ceil(totalLocal / DB_PAGE_SIZE) || 1;
                const localPageClamped = Math.min(localBackupPage, totalLocalPages);
                const localSlice = dbBackup.localBackups.slice((localPageClamped - 1) * DB_PAGE_SIZE, localPageClamped * DB_PAGE_SIZE);
                return (
                  <>
                    <div style={{ padding: '8px 18px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                        Local Backups ({totalLocal})
                      </div>
                      {totalLocal > 0 && (
                        <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                          Retention: {dbBackup.retentionDays?.local || 7} days local
                        </span>
                      )}
                    </div>
                    {totalLocal > 0 ? (
                      <>
                        <S3TableWrap>
                          <S3Table>
                            <thead>
                              <tr>
                                <th>Filename</th>
                                <th>Date</th>
                                <th>Time (IST)</th>
                                <th style={{ textAlign: 'right' }}>Size</th>
                              </tr>
                            </thead>
                            <tbody>
                              {localSlice.map((bk, idx) => (
                                <tr key={idx}>
                                  <td style={{ fontFamily: 'monospace', fontSize: '10px', color: '#475569' }}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" style={{ width: 10, height: 10, verticalAlign: -1, marginRight: 4 }}>
                                      <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
                                    </svg>
                                    {bk.filename}
                                  </td>
                                  <td style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>{bk.date}</td>
                                  <td style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>{bk.time}</td>
                                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{bk.sizeFormatted}</td>
                                </tr>
                              ))}
                            </tbody>
                          </S3Table>
                        </S3TableWrap>
                        {totalLocalPages > 1 && (
                          <PaginationWrap>
                            <PageBtn disabled={localPageClamped <= 1} onClick={() => setLocalBackupPage(p => Math.max(1, p - 1))}>Prev</PageBtn>
                            {Array.from({ length: totalLocalPages }, (_, i) => (
                              <PageBtn key={i + 1} $active={localPageClamped === i + 1} onClick={() => setLocalBackupPage(i + 1)}>{i + 1}</PageBtn>
                            ))}
                            <PageBtn disabled={localPageClamped >= totalLocalPages} onClick={() => setLocalBackupPage(p => Math.min(totalLocalPages, p + 1))}>Next</PageBtn>
                          </PaginationWrap>
                        )}
                      </>
                    ) : (
                      <div style={{ padding: '16px 18px', color: '#94a3b8', fontSize: '12px' }}>
                        No local backups found.
                      </div>
                    )}
                  </>
                );
              })()}

              {/* ── Database Backup Sync Log ── */}
              {dbBackup.lastSyncLog && (
                <>
                  <div style={{ padding: '8px 18px 0', fontSize: '11px', fontWeight: 600, color: '#475569' }}>Database Backup Log</div>
                  <SyncLogPre>{dbBackup.lastSyncLog}</SyncLogPre>
                </>
              )}
            </>
          ) : null}
        </TabPanel>
      )}

      {/* Notifications are now handled globally by BackupActivityToast in AdminDashboard */}
    </PageContainer>
  );
};

export default S3Settings;
